/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/layout/BottomNav.tsx
 *
 * Rôle :
 *   Barre de navigation mobile FIXÉE EN BAS, toujours visible sur les
 *   routes publiques (sauf splash, onboarding, scanner plein écran,
 *   modales, et pages admin qui ont leur propre sidebar).
 *   Pattern hérité de l'app JANE 2019 (cf. CLAUDE.md §8.11.3 + §8.12.2)
 *   qui avait un header mobile à 5 icônes : Mika confirme que c'est
 *   "le pattern parfait" pour TNE 2026.
 *
 * Contenu (5 items, ordre Stitch) :
 *   1. Accueil       → /mon-espace
 *   2. Programme     → /programme
 *   3. Carte         → /carte
 *   4. Mon badge     → /mon-badge
 *   5. Plus          → /plus (drawer secondaire, partenaires, médias,
 *                              à propos, profil, déconnexion)
 *
 * Accessibilité :
 *   - <nav role="navigation" aria-label="Navigation principale">
 *   - Chaque NavLink reçoit aria-current="page" quand actif (React Router).
 *   - Tap target ≥ 48 px (WCAG 2.5.5).
 *   - safe-area-inset-bottom (iPhone home indicator).
 *   - prefers-reduced-motion respecté.
 *
 * Routes où la nav est MASQUÉE (cf. usage côté AppLayout) :
 *   - /splash, /onboarding, /role, /inscription/:role
 *   - /scanner (plein écran caméra)
 *   - /admin/* (sidebar admin spécifique)
 *
 * Réutilisable :
 *   Composant 100 % autonome. Les libellés et chemins sont
 *   configurables via la prop `items` si on veut une nav adaptée à
 *   un autre rôle (ex : exposant connecté = scan + mes scans + déco).
 *
 * Voir aussi : CLAUDE.md §5 entrée 2026-05-11 (refonte nav) + §8.11 JANE.
 *
 * @author Mika
 * @since 2026-05-11
 */

import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
// FIX 2026-05-17 (Chantier B), nav conditionnée par feature flag.
// Sémantique « défaut ON » : un item disparaît UNIQUEMENT si son flag
// est explicitement coupé au backoffice.
import { useFeatureFlags } from '@/hooks/useFeatureFlag';
import styles from './BottomNav.module.css';

// ──────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────

export interface BottomNavItem {
    /** Chemin React Router (ex: '/programme'). */
    readonly to: string;
    /** Clé i18n du libellé court (4-8 caractères idéal). */
    readonly labelKey: string;
    /** Pictogramme emoji ou texte 1 caractère (visuel + clair sur mobile). */
    readonly icon: string;
    /** Optionnel : sous-paths qui doivent aussi marquer l'item actif. */
    readonly matchPrefixes?: readonly string[];
    /**
     * Optionnel : feature flag de visibilité (Chantier B). Si défini ET
     * explicitement coupé au backoffice, l'item disparaît. Absent /
     * inconnu / API HS → item visible (jamais de nav cassée).
     */
    readonly flag?: string;
}

export interface BottomNavProps {
    /** Items à afficher. Défaut = 5 items visiteur. */
    readonly items?: readonly BottomNavItem[];
    /** Masquer la nav (utile pour les pages plein écran). */
    readonly hidden?: boolean;
}

// ──────────────────────────────────────────────────────────────────────────
// Items par défaut (parcours visiteur, pattern JANE)
// ──────────────────────────────────────────────────────────────────────────

const DEFAULT_ITEMS: readonly BottomNavItem[] = [
    {
        to: '/mon-espace',
        labelKey: 'bottomNav.home',
        icon: '🏠',
        matchPrefixes: ['/mon-espace', '/'],
    },
    // 2026-10-04 (Mika) : « Le salon » (exposants, offres, ateliers) prend
    // la place de Programme, qui passe dans « Plus ». C'est le cœur du salon.
    {
        to: '/salon',
        labelKey: 'bottomNav.salon',
        icon: '🏢',
        matchPrefixes: ['/salon', '/exposants', '/postes', '/programme', '/atelier', '/mon-programme'],
    },
    {
        to: '/carte',
        labelKey: 'bottomNav.map',
        icon: '🗺️',
        matchPrefixes: ['/carte'],
        flag: 'carte.view',
    },
    {
        to: '/mon-badge',
        labelKey: 'bottomNav.badge',
        icon: '🎫',
        matchPrefixes: ['/mon-badge'],
        flag: 'badge.generation',
    },
    {
        to: '/plus',
        labelKey: 'bottomNav.more',
        icon: '⋯',
        matchPrefixes: ['/plus', '/medias', '/partenaires', '/a-propos', '/contact'],
    },
];

/**
 * 2026-09-23, phase d'inscription anticipee des candidats (Mika). Tant que le
 * salon est ferme au public, une personne inscrite n'a acces qu'a ce qui lui
 * appartient : son compte, son profil, son CV, son badge. Lui laisser la
 * navigation complete l'enverrait sur << Le salon se prepare >> a chaque
 * onglet, ce qui se lit comme une panne. Ces items remplacent alors les
 * items par defaut (cf. AppLayout).
 */
export const ITEMS_PHASE_CANDIDAT: readonly BottomNavItem[] = [
    {
        to: '/mon-compte',
        labelKey: 'bottomNav.account',
        icon: '🏠',
        matchPrefixes: ['/mon-compte', '/'],
    },
    {
        to: '/mon-profil',
        labelKey: 'bottomNav.profile',
        icon: '📄',
        matchPrefixes: ['/mon-profil'],
    },
    {
        to: '/mon-badge',
        labelKey: 'bottomNav.badge',
        icon: '🎫',
        matchPrefixes: ['/mon-badge'],
        flag: 'badge.generation',
    },
];

/**
 * 2026-10-05 (accès VIP) : la navigation d'un invité VIP. « Accueil » mène à
 * SON accueil (/vip) ; pas d'onglet badge (le sien l'attend à l'accueil, et
 * il le télécharge depuis /vip). Mikael - chef de projet - 2026-10-05
 */
export const ITEMS_VIP: readonly BottomNavItem[] = [
    {
        to: '/vip',
        labelKey: 'bottomNav.home',
        icon: '✦',
        matchPrefixes: ['/vip'],
    },
    {
        to: '/salon',
        labelKey: 'bottomNav.salon',
        icon: '🏢',
        matchPrefixes: ['/salon', '/exposants', '/postes', '/programme', '/atelier'],
    },
    {
        to: '/carte',
        labelKey: 'bottomNav.map',
        icon: '🗺️',
        matchPrefixes: ['/carte'],
        flag: 'carte.view',
    },
    {
        to: '/plus',
        labelKey: 'bottomNav.more',
        icon: '⋯',
        matchPrefixes: ['/plus', '/medias', '/partenaires', '/a-propos', '/contact'],
    },
];

/**
 * Détermine si l'item courant doit être marqué actif d'après le pathname.
 * Plus tolérant que le match exact de NavLink : utile pour les sous-routes
 * (ex : /atelier/foo doit garder "Programme" surligné).
 */
function isItemActive(
    item: BottomNavItem,
    currentPath: string,
): boolean {
    if (currentPath === item.to) return true;
    for (const prefix of item.matchPrefixes ?? []) {
        // '/' = match exact uniquement (sinon il matcherait toutes les routes)
        if (prefix === '/' && currentPath === '/') return true;
        if (prefix !== '/' && currentPath.startsWith(prefix)) return true;
    }
    return false;
}

// ──────────────────────────────────────────────────────────────────────────
// Composant
// ──────────────────────────────────────────────────────────────────────────

export function BottomNav({ items = DEFAULT_ITEMS, hidden = false }: BottomNavProps): JSX.Element | null {
    // Flags désactivés (défaut ON : on ne masque QUE ce qui est
    // explicitement coupé au backoffice). Le hook partage le cache
    // TanStack global, aucun fetch supplémentaire.
    const flagsQuery = useFeatureFlags();
    const visibleItems = useMemo<readonly BottomNavItem[]>(() => {
        const map = flagsQuery.data?.flags ?? {};
        return items.filter((it) => {
            if (it.flag === undefined) return true;
            const f = map[it.flag];
            // Masqué UNIQUEMENT si présent ET explicitement désactivé.
            return !(f && f.is_enabled === false);
        });
    }, [items, flagsQuery.data]);

    if (hidden) return null;

    // useLocation() évité ici, on laisse NavLink + render-prop gérer l'état
    // actif via son `isActive`, complété par notre logique matchPrefixes.
    return (
        <nav
            className={styles.bottomNav}
            aria-label="Navigation principale"
            data-testid="bottom-nav"
        >
            <ul className={styles.list}>
                {visibleItems.map((item) => (
                    <li key={item.to} className={styles.item}>
                        <NavLink
                            to={item.to}
                            end={item.to === '/'}
                            className={({ isActive }) => {
                                // Compose isActive standard (URL match exact)
                                // avec notre matchPrefixes (sous-routes).
                                const active =
                                    isActive ||
                                    isItemActive(item, window.location.pathname);
                                return [
                                    styles.link,
                                    active ? styles.linkActive : '',
                                ]
                                    .filter(Boolean)
                                    .join(' ');
                            }}
                        >
                            <span className={styles.icon} aria-hidden="true">
                                {item.icon}
                            </span>
                            <span className={styles.label}>
                                <FormattedMessage id={item.labelKey} />
                            </span>
                        </NavLink>
                    </li>
                ))}
            </ul>
        </nav>
    );
}

export default BottomNav;
